import { MapPin, PhoneCall } from 'lucide-react';

import Pagination from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Franchisee } from '@/types/franchisee.type';

interface FranchiseesTableProps {
  data: Franchisee[];
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const FranchiseesTable = ({
  data,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange,
}: FranchiseesTableProps) => {
  const renderFranchiseeCard = (franchisee: Franchisee) => (
    <div
      key={franchisee.id}
      className="flex flex-col gap-4 rounded-xl border border-border bg-white p-4"
    >
      <div>
        <p className="as-title-03 as-strong">{franchisee.name}</p>
        <p className="as-body-03 text-muted-foreground">{franchisee.email}</p>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <PhoneCall className="size-4" />
          <p className="as-body-03">{franchisee.phone}</p>
        </div>
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <MapPin className="size-4" />
          <p className="as-body-03">{franchisee.location}</p>
        </div>
      </div>
    </div>
  );

  const renderTableRow = (franchisee: Franchisee) => (
    <TableRow key={franchisee.id}>
      <TableCell className="px-4">{franchisee.name}</TableCell>
      <TableCell>{franchisee.email}</TableCell>
      <TableCell>{franchisee.phone}</TableCell>
      <TableCell>{franchisee.location}</TableCell>
    </TableRow>
  );

  return (
    <>
      <div className="space-y-4 md:hidden">
        {data.length === 0 ? (
          <div className="rounded-xl border border-border bg-white p-4 text-center text-muted-foreground">
            No franchisees found
          </div>
        ) : (
          data.map(renderFranchiseeCard)
        )}
        <div className="pt-2">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      </div>
      <div className="overflow-hidden rounded-xl border border-alpha-dark-5 bg-muted max-md:hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="as-title-04 px-4">Name</TableHead>
              <TableHead className="as-title-04">Email</TableHead>
              <TableHead className="as-title-04">Phone</TableHead>
              <TableHead className="as-title-04">Location</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="bg-white">
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-center text-muted-foreground"
                >
                  No franchisees found
                </TableCell>
              </TableRow>
            ) : (
              data.map(renderTableRow)
            )}
          </TableBody>
        </Table>
        <div className="border-t border-alpha-dark-5 px-4 py-3">
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      </div>
    </>
  );
};

export { FranchiseesTable };
